'use client'
import type React from 'react'
import { useState, useCallback, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { goToMailError, useSendApi } from '@/components/forms/hook'
import type { Seminar } from '@/types/microcms/seminar'
import type { Department } from '@/types/microcms/department'
import { ActionButton } from '@/components/parts/action-button'
import { useThanksForAppRouter } from '@/hooks/useThanksForAppRouter'
import {
  SeminarFormField,
  type SeminarFormValues,
  type SeminarScheduleItem
} from './SeminarFormField'

type SeminarFormProps = {
  seminar: Seminar
  departments?: Department[]
}

type FormValues = SeminarFormValues

/**
 * checkbox-group（複数選択）の値を、Salesforce の複数選択リストに合わせて
 * セミコロン区切りの 1 つの文字列にする（例: ChatGPT;Claude）。送信名は microCMS の fieldname のまま
 */
const toCheckboxGroupPayload = (
  form: Seminar['form'],
  data: FormValues
): Record<string, string> =>
  Object.fromEntries(
    (form ?? [])
      .filter((field) => field.fieldId === 'checkbox-group')
      .map((field) => {
        const name = field.fieldname || field.fieldId
        const value = data[name]
        // 選択肢が 1 つだけだと RHF は配列でなく値の文字列（未選択は false）を返す
        const joined = Array.isArray(value)
          ? value.join(';')
          : typeof value === 'string'
            ? value
            : ''
        return [name, joined]
      })
  )

const defaultValues = {
  salesforceId: '',
  first_view_route: '',
  last_view_route: ''
}

/**
 * セミナーの申込・資料ダウンロードフォーム。Figma: rail（Type=form）の form 以下。
 * 項目は microCMS の seminar.form から組み立てる。カードの面と見出しは RailCard 側で描く。
 */
const SeminarForm: React.FC<SeminarFormProps> = ({
  seminar,
  departments = []
}) => {
  const [currentSalesforceId, setCurrentSalesforceId] = useState(
    seminar.salesforceId?.replace('https://info.lawgue.com', '') || ''
  )
  const { sendApi } = useSendApi(
    '/lawgue-seminar-v2',
    '/thanks_seminar/',
    (json, res) => json?.message === 'Success' && res.status === 200
  )
  const [isButtonClicked, setIsButtonClicked] = useState(false)
  const [firstViewRoute, setFirstViewRoute] = useState('')
  const [lastViewRoute, setLastViewRoute] = useState('')
  const { pushThanks } = useThanksForAppRouter()
  const [scheduleItems, setScheduleItems] = useState<SeminarScheduleItem[]>([])

  // フォームから開催日程データを取得（定例セミナー用）
  useEffect(() => {
    // まず seminar.seminarSchedules から取得を試みる
    if (
      seminar.seminarSchedules &&
      Array.isArray(seminar.seminarSchedules) &&
      seminar.seminarSchedules.length > 0
    ) {
      const schedules = seminar.seminarSchedules.map((schedule, index) => ({
        fieldId: `schedule-${index}`,
        preferredDay: schedule.preferredDay,
        salesforceId: schedule.salesforceId
      }))
      setScheduleItems(schedules)
      return
    }

    // フォームフィールドから seminarSchedules を抽出
    if (seminar.form && Array.isArray(seminar.form)) {
      const scheduleFields = seminar.form.filter(
        (field: any) =>
          field.fieldId === 'seminarSchedules' &&
          field.preferredDay &&
          field.salesforceId
      )

      if (scheduleFields.length > 0) {
        const schedules = scheduleFields.map((field: any, index) => ({
          fieldId: `schedule-${index}`,
          preferredDay: field.preferredDay,
          salesforceId: field.salesforceId
        }))
        setScheduleItems(schedules)
        return
      }
    }
  }, [seminar.seminarSchedules, seminar.form])

  useEffect(() => {
    const fullPath = `${window.location.pathname}${window.location.search}`

    // firstViewURLが未設定の場合のフォールバック処理
    let firstViewURL = sessionStorage.getItem('firstViewURL')
    if (!firstViewURL) {
      firstViewURL = fullPath
      sessionStorage.setItem('firstViewURL', firstViewURL)
    }

    setFirstViewRoute(firstViewURL)
    setLastViewRoute(fullPath)
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors, isLoading }
  } = useForm<FormValues>({
    defaultValues: {
      ...defaultValues,
      salesforceId:
        seminar.salesforceId?.replace('https://info.lawgue.com', '') || '',
      first_view_route: '/',
      last_view_route: '/seminar/' + seminar.id || ''
    }
  })

  const handleOnSubmit: SubmitHandler<FormValues> = useCallback(
    async (data) => {
      setIsButtonClicked(true)
      try {
        const selectElement = document.querySelector(
          '#select_form5'
        ) as HTMLSelectElement
        const selectedValue = selectElement?.value
        const selectedSchedule = scheduleItems.find(
          (s) => s.fieldId === selectedValue
        )
        const currentLastViewRoute = location.href.split(location.host)[1]
        const { preferredDay, ...dataWithoutPreferredDay } = data

        const apiPayload = JSON.stringify({
          ...dataWithoutPreferredDay,
          ...toCheckboxGroupPayload(seminar.form, data),
          first_view_route: firstViewRoute,
          last_view_route: currentLastViewRoute,
          salesforceId: currentSalesforceId,
          select_form5: selectedSchedule?.preferredDay || ''
        })
        const isSent = await sendApi(apiPayload)
        // 失敗時は sendApi が送信エラーページへ遷移させるので、サンクスへは進まない
        if (isSent) pushThanks('thanks_seminar')
      } catch (error) {
        console.error('Error during form submission:', error)
        goToMailError()
      } finally {
        setIsButtonClicked(false)
      }
    },
    [
      sendApi,
      firstViewRoute,
      lastViewRoute,
      currentSalesforceId,
      pushThanks,
      scheduleItems,
      seminar.form
    ]
  )

  return (
    <div className="flex flex-col gap-8">
      <form
        className="flex flex-col gap-8"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <fieldset className="flex flex-col gap-6">
          <input type="hidden" name="first_view_route" value={firstViewRoute} />
          <input type="hidden" name="last_view_route" defaultValue="/contact" />
          <input
            type="hidden"
            value={currentSalesforceId}
            {...register('salesforceId')}
          />
          {seminar.form &&
          Array.isArray(seminar.form) &&
          seminar.form.length > 0 ? (
            seminar.form
              .filter((field) => field.fieldId !== 'seminarSchedules') // seminarSchedulesを除外
              .map((field, index, forms) => (
                // fieldId は microCMS のカスタムフィールド型名（例: 'input'）で
                // 項目ごとに一意ではないため index を併せて key にする。
                // 名（firstname）は姓の行で描くので null になり、余白を生まないよう DOM を挟まない
                <SeminarFormField
                  key={`${field.fieldId}-${index}`}
                  form={field}
                  index={index}
                  forms={forms}
                  register={register}
                  errors={errors}
                  departments={departments}
                  scheduleItems={scheduleItems}
                  onSalesforceIdChange={setCurrentSalesforceId}
                />
              ))
          ) : (
            <p className="text-center text-sm leading-compact text-ink-subtle">
              フォームの設定がありません
            </p>
          )}
        </fieldset>
        <p className="text-sm leading-compact text-ink">
          同業他社および当社の競合企業にあたる企業の方の資料請求はご遠慮させていただきます。何卒ご了承ください。
        </p>
        <ActionButton
          type="submit"
          label={isButtonClicked ? '送信中' : '送信する'}
          disabled={isButtonClicked || isLoading}
          block
        />
      </form>
    </div>
  )
}

export default SeminarForm
